import axios from "axios";
import { isCustomerOnboardingRoute } from './customerRoutes';
import { API_ENDPOINTS, METHOD_TYPE } from "./apiUrls";
import { encryptData, decryptData } from "./encryption";
import { toast } from "react-toastify";
import { buildLocationLogHeaders } from "./location";

// Helper function to check if token is expired
const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        const { exp } = JSON.parse(jsonPayload);
        // Check if token expires in less than 2 minutes (120 seconds buffer)
        return exp && (exp * 1000 - Date.now() < 120000);
    } catch (error) {
        console.warn('[API] Failed to decode token:', error);
        return true;
    }
};

// Helper function to decode token payload
const decodeTokenPayload = (token) => {
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        return null;
    }
};

const getApiBaseURL = () => {
    const configuredBaseURL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BASE_URL || '';
    if (configuredBaseURL) {
        return configuredBaseURL.endsWith('/') ? configuredBaseURL : `${configuredBaseURL}/`;
    }

    if (typeof window !== 'undefined') {
        const { origin, hostname } = window.location;
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return "http://localhost:4000/api/";
        }
        return `${origin}/api/`;
    }

    return "http://localhost:4000/api/";
};

const api = axios.create({
    baseURL: getApiBaseURL(),
    headers: {
        "Content-Type": "application/json",
        // Add any common headers here
    },
});

// HTTPS already protects transport. The additional legacy AES envelope is now
// opt-in so a frontend/backend key mismatch cannot break client mutations.
const encryptApiPayloads = process.env.REACT_APP_ENCRYPT_API_PAYLOADS === 'true';

const refreshApi = axios.create({
    baseURL: getApiBaseURL(),
    headers: {
        "Content-Type": "application/json",
        platform: "web",
    },
});

// Flag to track if token refresh is in progress at request level
let isRefreshingToken = false;
let refreshSubscribers = [];

// Global flag to track if user was logged out by activity tracker
// This prevents race conditions where activity tracker logs out but
// pending API calls try to refresh the token
export let isGloballyLoggedOut = false;

export const setGlobalLogoutState = (value) => {
    isGloballyLoggedOut = value;
};

export const clearGlobalLogoutState = () => {
    isGloballyLoggedOut = false;
};

const subscribeTokenRefresh = (resolve, reject) => {
    refreshSubscribers.push({ resolve, reject });
};

const onTokenRefreshed = (newToken) => {
    refreshSubscribers.forEach(({ resolve }) => resolve(newToken));
    refreshSubscribers = [];
};

const onTokenRefreshFailed = (error) => {
    refreshSubscribers.forEach(({ reject }) => {
        if (typeof reject === 'function') {
            reject(error);
        }
    });
    refreshSubscribers = [];
};

const refreshAccessToken = async () => {
    // Check if user was already logged out by activity tracker
    if (isGloballyLoggedOut) {
        throw new Error('User already logged out');
    }

    const refreshToken = localStorage.getItem('refresh') || localStorage.getItem('refreshToken');
    if (!refreshToken) {
        throw new Error('No refresh token available');
    }

    // Double-check in case logout happened while we were waiting
    if (isGloballyLoggedOut) {
        throw new Error('User already logged out');
    }

    const response = await refreshApi.post(API_ENDPOINTS.refreshToken, { refreshToken });
    if (response.data.status) {
        const { token, refresh } = response.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('refresh', refresh);
        localStorage.setItem('refreshToken', refresh);
        return token;
    }
    throw new Error('Token refresh failed');
};

export const ensureValidAccessToken = async () => {
    const token = localStorage.getItem('token');

    if (token && !isTokenExpired(token)) {
        return token;
    }

    if (isGloballyLoggedOut) {
        throw new Error('User already logged out');
    }

    const refreshToken = localStorage.getItem('refresh') || localStorage.getItem('refreshToken');
    if (!refreshToken) {
        throw new Error('No refresh token available');
    }

    if (isRefreshingToken) {
        return new Promise((resolve, reject) => {
            subscribeTokenRefresh(resolve, reject);
        });
    }

    isRefreshingToken = true;

    try {
        const newToken = await refreshAccessToken();
        onTokenRefreshed(newToken);
        return newToken;
    } catch (error) {
        onTokenRefreshFailed(error);
        throw error;
    } finally {
        isRefreshingToken = false;
    }
};

// react-router keeps navigation state under history.state.usr; a page opened straight
// from a link that carries the token in its address has it in the query string instead.
const currentWebformToken = () => {
    if (typeof window === 'undefined') return '';
    try {
        return window.history.state?.usr?.webformToken
            || new URLSearchParams(window.location.search).get('webformToken')
            || '';
    } catch (_) {
        return '';
    }
};

api.interceptors.request.use(async function (config) {
    // A customer on an emailed onboarding link has no staff session. Reading a staff
    // token left in this browser made every request fail: an expired one was
    // refreshed, failed and redirected to /login, and the gateway rejects any
    // request carrying an expired Bearer token even for public actions.
    let token = isCustomerOnboardingRoute() ? null : localStorage.getItem('token');

    // Check if token is expired or about to expire, and refresh it proactively
    // Skip for auth-related endpoints to avoid loops
    const isAuthEndpoint = config.url && (
        config.url.includes('refreshToken') ||
        config.url.includes('login') ||
        config.url.includes('verifyLoginOtp') ||
        config.url.includes('loginWithGoogleAuthenticator')
    );

    if (token && !isAuthEndpoint && isTokenExpired(token) && !config._skipTokenCheck) {
        config._skipTokenCheck = true; // Prevent infinite loops

        try {
            token = await ensureValidAccessToken();
        } catch (error) {
            // Refresh failed - clear storage and let response interceptor handle redirect
            setGlobalLogoutState(true);
            localStorage.clear();
            toast.error('Your session has expired. Please log in again.', {
                toastId: 'session-expired',
                autoClose: 4000,
            });
            // Redirect will be handled by the app level
            if (typeof window !== 'undefined') {
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }
    }

    if (token && config.headers.Authorization !== '') {
        config.headers.Authorization = "Bearer " + token;
    }
    config.headers.platform = 'web';

    // A customer's page proves which onboarding link it came from with the link's public
    // token. Requests that did not pass it reached the gateway as anonymous and got 401,
    // so send it with every request made from a customer page.
    if (isCustomerOnboardingRoute() && !config.headers.publictoken) {
        const webformToken = currentWebformToken();
        if (webformToken) config.headers.publictoken = webformToken;
    }

    const locationHeaders = buildLocationLogHeaders();
    Object.entries(locationHeaders).forEach(([key, value]) => {
        config.headers[key] = value;
    });

    // Retain the plain body so an explicitly enabled encrypted request can be
    // retried once if a deployment has mismatched AES keys.
    if (config.data && !(config.data instanceof FormData) && !config._plainData) {
        config._plainData = config.data;
    }

    if (encryptApiPayloads && config.data && !(config.data instanceof FormData) && !config._skipEncryption) {
        const encryptedString = encryptData(config.data);
        // Wrap in a JSON envelope so Moleculer's JSON body parser accepts it.
        // The backend onBeforeCall hook will detect and decrypt the _enc key.
        config.data = { _enc: encryptedString };
        config.headers['Content-Type'] = 'application/json';
    }

    return config;
});

let isRefreshing = false;
let failedQueue = [];
let responseInterceptorId = null;

const SINGLE_SESSION_ERROR_PATTERNS = [
    'another device',
    'another browser',
    'different device',
    'active session',
    'already logged in',
    'logged in somewhere else',
    'session revoked',
    'session superseded',
    'invalid session',
    'single session',
];

const isSingleSessionError = (message) => {
    const normalizedMessage = (message || '').toString().toLowerCase();
    return SINGLE_SESSION_ERROR_PATTERNS.some((pattern) => normalizedMessage.includes(pattern));
};

const processQueue = (error, token) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

const shouldIntercept = (error) => {
    try {
        return error.response.status === 401
    } catch (e) {
        return false;
    }
};

const expiredRefreshToken = (error, url) => {
    try {
        return error.response.status === 401 && API_ENDPOINTS.refreshToken === url
    } catch (e) {
        return false;
    }
};

const attachTokenToRequest = (request, token) => {
    // if (request.url === "/engine/article-generator/") {
    //   request.headers['Content-Type'] = 'multipart/form-data';
    // }
    request.headers['Authorization'] = 'Bearer ' + token;
};

/**
 * Helper: Parse the decrypted payload.
 * Determines if the decrypted string was the entire response object (containing status/message)
 * or just the `data` portion of the response.
 */
const parseDecryptedPayload = (originalData, decryptedPayload) => {
    if (decryptedPayload && typeof decryptedPayload === 'object' && !Array.isArray(decryptedPayload)) {
        if ('status' in decryptedPayload || 'message' in decryptedPayload || 'data' in decryptedPayload) {
            // The decrypted string contained the full response object
            return { ...originalData, ...decryptedPayload };
        }
    }
    // The decrypted string contained just the data portion (or an array, or primitive)
    return { ...originalData, data: decryptedPayload };
};

/**
 * Helper: Decrypt the error response data and extract a readable error message.
 * API error responses come as { data: "U2FsdGVkX18..." } where the encrypted
 * string contains the actual error object with a `message` field.
 */
const decryptErrorResponse = (error) => {
    try {
        if (!error?.response?.data) return;

        const errorData = error.response.data;

        // Case 1: The entire response is { data: "encrypted_string" }
        if (errorData.data && typeof errorData.data === 'string') {
            const decryptedPayload = decryptData(errorData.data);
            if (decryptedPayload !== undefined && decryptedPayload !== errorData.data) {
                error.response.data = parseDecryptedPayload(errorData, decryptedPayload);
            }
        }

        // Case 2: The message field itself might be encrypted
        if (error.response.data.message && typeof error.response.data.message === 'string') {
            const decryptedMessage = decryptData(error.response.data.message);
            if (decryptedMessage !== undefined && decryptedMessage !== error.response.data.message && typeof decryptedMessage === 'string') {
                error.response.data.message = decryptedMessage;
            }
        }
    } catch (decryptError) {
        console.warn('[API] Failed to decrypt error response:', decryptError);
    }
};

export const SERVER_UNAVAILABLE_MESSAGE = 'The server is not responding right now. Please try again in a minute.';

/**
 * Helper: Replace a proxy's HTML error page with a readable message.
 * When the API is down nginx answers 502/503/504 with an HTML page, and every screen that
 * shows error.message put that markup in a toast.
 */
export const normalizeGatewayError = (error) => {
    const response = error?.response;
    if (!response) return;
    const body = response.data;
    const isHtml = typeof body === 'string' && /^\s*<(!doctype|html|head|body)/i.test(body);
    const isGatewayFailure = [502, 503, 504].includes(response.status)
        && (body == null || typeof body === 'string');
    if (!isHtml && !isGatewayFailure) return;
    response.data = { status: false, message: SERVER_UNAVAILABLE_MESSAGE };
    error.message = SERVER_UNAVAILABLE_MESSAGE;
};

export const responseInterceptor = (navigate) => {
    // Eject existing interceptor to prevent duplicates on re-mounts
    if (responseInterceptorId !== null) {
        api.interceptors.response.eject(responseInterceptorId);
    }

    responseInterceptorId = api.interceptors.response.use(
        (response) => {
            // Responses are now plain JSON — no decryption needed.
            // If somehow a response still has an encrypted data field (legacy), try to decrypt it.
            if (response.data && response.data.data && typeof response.data.data === 'string' &&
                (response.data.data.startsWith('OOMERO_ENC:') || response.data.data.startsWith('U2FsdGVkX1'))) {
                const decryptedPayload = decryptData(response.data.data);
                if (decryptedPayload !== undefined && decryptedPayload !== response.data.data) {
                    response.data = parseDecryptedPayload(response.data, decryptedPayload);
                }
            }
            return response;
        },
        async (error) => {
            const originalRequest = error.config;
            if (!originalRequest) return Promise.reject(error);

            // Check if user was already logged out - don't attempt refresh
            if (isGloballyLoggedOut) {
                return Promise.reject(error);
            }

            // 1. Decrypt error response so all downstream code gets readable messages
            decryptErrorResponse(error);
            normalizeGatewayError(error);

            const status = error.response ? error.response.status : null;

            const payloadErrorMessage = typeof error.response?.data === 'string'
                ? error.response.data
                : (error.response?.data?.message || error.response?.data?.error || '');
            const isEncryptedPayloadFailure = status === 400
                && String(payloadErrorMessage).toLowerCase().includes('invalid encrypted payload');

            if (
                isEncryptedPayloadFailure
                && originalRequest._plainData
                && !originalRequest._retriedWithoutEncryption
            ) {
                originalRequest._retriedWithoutEncryption = true;
                originalRequest._skipEncryption = true;
                originalRequest.data = originalRequest._plainData;
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers['Content-Type'] = 'application/json';
                return api(originalRequest);
            }

            // Check if this is the refresh request itself failing
            // Customer onboarding pages have no staff session to refresh or expire.
            // Let the page show its own error instead of the staff login screen.
            if (status === 401 && isCustomerOnboardingRoute()) {
                return Promise.reject(error);
            }

            const isRefreshTokenRequest = originalRequest.url && (
                originalRequest.url.includes('refreshToken') ||
                originalRequest.url.includes('refresh-token') ||
                (API_ENDPOINTS.refreshToken && originalRequest.url.includes(API_ENDPOINTS.refreshToken))
            );

            const isUnauthorized = status === 401 && !originalRequest._retry;

            // 2. Extract Error Message robustly (handles object vs string, message vs error field)
            const responseData = error.response?.data || {};
            const errorMessageRaw = typeof responseData === 'string' ? responseData : (responseData.message || responseData.error || '');
            const errorMessage = errorMessageRaw.toString().toLowerCase();

            // The gateway names a session replaced by a sign-in elsewhere (14 Oct 2026).
            const isSingleSessionFailure = responseData.type === 'SESSION_SUPERSEDED' || isSingleSessionError(errorMessage);

            // 3. Immediate Session Expiry (Single Session Logout)
            // If explicitly logged from another device OR if the refresh call itself is returning 401
            if (isUnauthorized && (isSingleSessionFailure || isRefreshTokenRequest)) {
                toast.error(errorMessageRaw || 'Your session has expired. Please log in again.', {
                    toastId: 'session-expired',
                    autoClose: 4000,
                });
                localStorage.clear();
                setGlobalLogoutState(true);
                if (navigate) navigate('/login');
                return Promise.reject(error);
            }

            // 4. Token Refresh Logic for normal requests
            if (isUnauthorized) {
                originalRequest._retry = true;

                // Check global logout state again before attempting refresh
                if (isGloballyLoggedOut) {
                    return Promise.reject(error);
                }

                const refreshToken = localStorage.getItem('refresh') || localStorage.getItem('refreshToken');

                if (!refreshToken) {
                    toast.error('Your session has expired. Please log in again.', {
                        toastId: 'session-expired',
                        autoClose: 4000,
                    });
                    localStorage.clear();
                    setGlobalLogoutState(true);
                    if (navigate) navigate('/login');
                    return Promise.reject(error);
                }

                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        attachTokenToRequest(originalRequest, token);
                        return api(originalRequest);
                    }).catch(err => Promise.reject(err));
                }

                isRefreshing = true;

                return new Promise((resolve, reject) => {
                    // Final check before making refresh request
                    if (isGloballyLoggedOut) {
                        isRefreshing = false;
                        reject(new Error('User logged out'));
                        return;
                    }

                    refreshApi.post(API_ENDPOINTS.refreshToken, { refreshToken })
                        .then(({ data }) => {
                            if (data.status) {
                                const { token, refresh } = data.data;
                                // Save both keys to ensure compatibility with different parts of the app
                                localStorage.setItem('token', token);
                                localStorage.setItem('refresh', refresh);
                                localStorage.setItem('refreshToken', refresh);

                                attachTokenToRequest(originalRequest, token);
                                processQueue(null, token);
                                resolve(api(originalRequest));
                            } else {
                                throw new Error('Refresh failed');
                            }
                        })
                        .catch((err) => {
                            processQueue(err, null);
                            toast.error('Your session has expired. Please log in again.', {
                                toastId: 'session-expired',
                                autoClose: 4000,
                            });
                            localStorage.clear();
                            setGlobalLogoutState(true);
                            if (navigate) navigate('/login');
                            reject(err);
                        })
                        .finally(() => {
                            isRefreshing = false;
                        });
                });
            }

            return Promise.reject(error);
        }
    );
    return responseInterceptorId;
};

export default api;



